import {
  Component,
  AfterViewInit,
  OnInit,
  ElementRef,
  ViewChild,
  NgZone,
  HostListener,
  Renderer2,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
  filter,
  tap,
  map,
  switchMap,
  take
} from 'rxjs/operators';

import {
  Logout,
  AuthUserStore,
  AuthStore,
  BrowserStorageService,
  getActiveTenant$,
  PermissionTypes,
  AppConfigSettings,
  CoreService,
  DataDogService,
  PendoService,
  UserNotificationAction,
  UserState,
  LoginAuthGuard
} from '@arc/arc-shared-frontend';

import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import {
  Router,
  ActivatedRoute,
  Params,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  Event,
  RouterEvent
} from '@angular/router';
import { MenuItem } from '@arc/aureus';
import { Observable, Subject, combineLatest } from 'rxjs';
import { SubSink } from 'subsink';
import { ScrollPanel } from 'primeng/scrollpanel';
import { PrimeNGConfig } from 'primeng/api';
import { RefreshService } from './authentication/services/refresh.service';

enum MenuOrientation {
  STATIC,
  OVERLAY,
  SLIM,
  HORIZONTAL
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit, OnInit {
  primaryNav: MenuItem[];
  secondaryNav: MenuItem[];

  profileItems: MenuItem[];

  showTenantSwitcher = false;
  showTenantSwitcherButton$;
  notifications = [];
  stickyNotifications = [];
  isloggedin$: Observable<boolean>;
  hasMultipleTenants$: Observable<boolean> = this.store.pipe(
    select(AuthUserStore),
    map(
      user =>
        user &&
        user.user &&
        user.user.tenants &&
        Array.isArray(user.user.tenants) &&
        user.user.tenants.length > 1
    )
  );
  tenant$;
  layoutCompact = true;

  isBusy$;
  showSessionTimeOut$: Observable<boolean>;

  layoutMode: MenuOrientation = MenuOrientation.HORIZONTAL;

  _subSink = new SubSink();
  darkMenu = false;
  profileMode = 'inline';

  overlayMenuActive: boolean;

  layoutContainer: HTMLDivElement;

  layoutMenuScroller: HTMLDivElement;

  menuClick: boolean;

  resetMenu: boolean;

  menuHoverActive: boolean;

  navigationEnded$ = new Subject<boolean>();
  navVisible$: Observable<boolean>;
  landedInLogin = false;
  visitedLogin = false;

  testTenant = false;

  testTenantMode$;

  @ViewChild('layoutContainer', { static: true })
  layourContainerViewChild: ElementRef;

  @ViewChild(ScrollPanel)
  layoutMenuScrollerViewChild: ScrollPanel;

  changeProfileItem = {
    command: () => this.gotoProfile(),
    icon: 'account_box',
    label: 'ARC_COMPOSITE_MENU_PROFILE',
    id: 'profile-item'
  };
  changeCompanyItem = {
    command: () => this.toggleSwitchTenant(),
    icon: 'swap_calls',
    label: 'ARC_COMPOSITE_MENU_CHANGE_COMPANY',
    id: 'change-company-item',
    visible$: this.store.pipe(
      select(AuthUserStore),
      map(
        user =>
          user &&
          user.user &&
          user.user.tenants &&
          Array.isArray(user.user.tenants) &&
          user.user.tenants.length > 1
      )
    )
  };

  logOutItem = {
    command: () => this.logout(),
    icon: 'power_settings_new',
    label: 'ARC_COMPOSITE_MENU_LOGOUT',
    id: 'logout-item'
  };

  signupItem = {
    label: 'Sign Up',
    icon: 'assignment_ind',
    routerLink: ['/signup']
  };

  roleListItem = {
    label: 'roles',
    icon: 'verified_user',
    routerLink: ['/roleslist']
  };

  get testTenantToggleIcon(): string {
    return this.testTenant ? 'toggle_off' : 'toggle_on';
  }

  get testTenantToggleLabel(): string {
    return `Switch to ${this.testTenant ? 'Production' : 'Test'} Mode`;
  }

  get showBlockUILoading() {
    return this.coreService.isLoading;
  }
  set showBlockUILoading(value) {
    this.coreService.isLoading = value;
  }

  get showBackDrop() {
    return this.coreService.showBackDrop;
  }

  set showBackDrop(value) {
    this.coreService.showBackDrop = value;
  }

  private _switcherOkClicked = false;
  private _switcherButtonClicked = false;

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {}

  constructor(
    public renderer: Renderer2,
    private store: Store<any>,
    public zone: NgZone,
    public httpClient: HttpClient,
    public storageService: BrowserStorageService,
    public router: Router,
    private translationService: TranslateService,
    private permissionDefinition: PermissionTypes,
    private appConfigSetting: AppConfigSettings,
    private activatedRoute: ActivatedRoute,
    private coreService: CoreService,
    private dataDogService: DataDogService,
    private primeNgConfig: PrimeNGConfig,
    private pendoService: PendoService, //although the pendoService doesn't appear to be used, we need it in the constructor in order for pendo to work properly
    private refreshService: RefreshService,
    private loginAuthGuard: LoginAuthGuard
  ) {
    this.profileItems = [
      this.changeProfileItem,
      this.changeCompanyItem,
      this.logOutItem
    ];

    this.isloggedin$ = this.store.pipe(
      select(AuthStore),
      map(user => {
        return user.user.loggedIn;
      })
    );

    this.navVisible$ = combineLatest([
      this.isloggedin$,
      this.navigationEnded$.asObservable()
    ])
    .pipe(
      map(([loggedIn, navigationEnd]) => {
        if (this.landedInLogin) {
          return false;
        }
        
        if (this.visitedLogin) {
          if (loggedIn && navigationEnd) {
            // If in the past navigation event we visited login, but this current navigation is ending, then remove that flag
            this.visitedLogin = false;

            return true;
          }
        } else {
          if (loggedIn) {
            return true;
          }
        }

        return false;
      })
    );
    
    this.tenant$ = getActiveTenant$(this.store).pipe(
      switchMap(x => {
        return this.permissionDefinition
          .isPermitted$(
            'arc-permission-section-tenant-management-applicationandfeaturemanagement:read'
          )
          .pipe(map(t => t && x.isPartner));
      })
    );

    this.showSessionTimeOut$ = this.store.pipe(
      select(AuthUserStore),
      map(x => x.sessionTimeOutPopUp)
    );

    this._subSink.add(
      router.events
        .pipe(
          tap((routerEvent: Event|RouterEvent) => {
            this.checkRouterEvent(routerEvent);
          })
        )
        .subscribe()
    );
  }

  ngAfterViewInit() {
    this.layoutContainer = <HTMLDivElement>(
      this.layourContainerViewChild.nativeElement
    );
    if (this.layoutMenuScrollerViewChild) {
      setTimeout(() => {
        this.layoutMenuScrollerViewChild.moveBar();
      }, 100);
    }
  }

  ngOnInit() {
    try {
      if (!localStorage) {
        this.store.dispatch(
          new UserNotificationAction({
            type: 'warning',
            message: this.translationService.instant(
              'ARC_COMPOSITE_NO_LOCAL_STORAGE_ERROR'
            )
          })
        );
      }
    } catch (e) {
      this.store.dispatch(
        new UserNotificationAction({
          type: 'warning',
          message: this.translationService.instant(
            'ARC_COMPOSITE_NO_LOCAL_STORAGE_ERROR'
          )
        })
      );
    }

    if (!location.pathname.startsWith('/login')) {
      this.initializeStore();
    }  

    this._subSink.add(
      getActiveTenant$(this.store)
        .pipe(
          map(x => {
            return x.isTestTenant;
          })
        )
        .subscribe(isTestTenantMode => {
          this.testTenant = isTestTenantMode;
        })
    );

    this._subSink.add(
      this.store
        .pipe(select(AuthUserStore))
        .subscribe((dataDogContext: UserState) => {
          if (
            dataDogContext.activeTenant?.tenantType === 'standard' &&
            this.appConfigSetting.getconfig('isDataDogLogging') === 'true'
          ) {
            this.dataDogService.initializeRum(dataDogContext);
            this.dataDogService.initializeLogging();
            this.dataDogService.setUser(dataDogContext);
          }
        })
    );

    this._subSink.add(
      this.store.pipe(select(AuthUserStore)).subscribe((x: any) => {
        if (x && x.user && x.user.defaultLanguage) {
          const lang = (x.user.defaultLanguage as string).toLowerCase();
          const userDefaultLang = lang === 'en-us' ? 'en' : lang;
          
          // Store user lang locally
          this.storageService.setItem('arc-userDefaultLang', userDefaultLang);
          this.pendoService.includeAdditionalContext({}, {profileLanguage: this.normalizeLanguageLocale(userDefaultLang)})

          this.translationService.use(userDefaultLang).subscribe({
            // next: (v) => console.info(`Successfully changed '${userDefaultLang}' language.`),
            error: (e) => console.error(`Problem with '${userDefaultLang}' language change.`)
          });
        } else {
          const userDefaultLang = this.storageService.getItem('arc-userDefaultLang');
          
          if (userDefaultLang && userDefaultLang !== '') {
            this.translationService.use(userDefaultLang).subscribe({
              // next: (v) => console.info(`Successfully changed '${userDefaultLang}' language.`),
              error: (e) => console.error(`Problem with '${userDefaultLang}' language change.`)
            });
          } else {
            this.translationService.use('en').subscribe({
              // next: (v) => console.info(`Successfully changed 'en' language.`),
              error: (e) => console.error(`Problem with 'en' language change.`)
            });
          }
        }
        this.translationService
          .get('primeng')
          .subscribe(res => this.primeNgConfig.setTranslation(res));
      })
    );

    this._subSink.add(
      this.activatedRoute.queryParams.subscribe(x => {
        if (x) {
          this.showTenantSwitcher = x.displayChangeCompany === 'true';
        }
      })
    );
  }

  initializeStore() {
    const loginParams = new HttpParams({
      fromObject: {
        redirectUrl: location.pathname,
        refresh: true
      }
    });

    this._subSink.add(
      this.loginAuthGuard
      .guard(loginParams)
      .subscribe()
    );
  }

  isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight);
  }

  getOffset(el) {
    const rect = el.getBoundingClientRect();

    return {
      top:
        rect.top +
        (window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0),
      left:
        rect.left +
        (window.pageXOffset ||
          document.documentElement.scrollLeft ||
          document.body.scrollLeft ||
          0)
    };
  }

  onMenuClick($event) {
    this.menuClick = true;
    this.resetMenu = false;
  }

  logout() {
    this.refreshService.isBypass$.next(false);
    this.store.dispatch(new Logout(''));
  }

  toggleSwitchTenant() {
    this._switcherOkClicked = false;
    this._switcherButtonClicked = false;
    if (!this.showTenantSwitcher) {
      const queryParams: Params = {
        displayChangeCompany: this.showTenantSwitcher ? null : 'true'
      };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: queryParams,
        queryParamsHandling: 'merge' // remove to replace all query params by provided
      });
    }

    this.showTenantSwitcher = !this.showTenantSwitcher;
  }

  toggleSwitchTenantByButton($event) {
    this._switcherOkClicked = false;
    this._switcherButtonClicked = true;
    if (!$event) {
      const queryParams: Params = {
        displayChangeCompany: this.showTenantSwitcher ? null : 'true'
      };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: queryParams,
        queryParamsHandling: 'merge' // remove to replace all query params by provided
      });
    }
    if ($event) {
      this._switcherOkClicked = $event;
    }
    this.showTenantSwitcher = !this.showTenantSwitcher;
  }

  switcherDismissed($event) {
    if (!this._switcherButtonClicked) {
      const queryParams: Params = {
        displayChangeCompany: null
      };
      this._switcherButtonClicked = false;
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: queryParams,
        queryParamsHandling: 'merge' // remove to replace all query params by provided
      });
    }
  }

  gotoProfile() {
    this.store
      .pipe(
        select(AuthUserStore),
        filter(x => !!x),
        take(1)
      )
      .subscribe(x => {
        this.router.navigate(['/profile']);
      });
  }

  addItem(items: MenuItem[], newItem: MenuItem) {
    if (items.findIndex(x => x.id === newItem.id) < 0) {
    }
  }

  removeItem(items: MenuItem[], newItem: MenuItem) {}

  checkRouterEvent(routerEvent: Event|RouterEvent): void {
    // Login - authentication transition UI checks
    if (routerEvent instanceof NavigationStart || routerEvent instanceof NavigationEnd) {
      if (routerEvent instanceof NavigationStart && routerEvent.url.includes('/login')) {
        this.landedInLogin = true;
      }

      if (routerEvent instanceof NavigationStart && !routerEvent.url.includes('/login')) {
        // Check if we previously visited login
        if (this.landedInLogin) {
          this.visitedLogin = true;
        }
        this.landedInLogin = false;
      }

      if (routerEvent instanceof NavigationStart) {
        this.navigationEnded$.next(false);
      }
      if (routerEvent instanceof NavigationEnd) {
        this.navigationEnded$.next(true);
      }
    }

    if (routerEvent instanceof NavigationStart) {
      if (routerEvent.url.includes('/collections/')) {
        this.showBlockUILoading = true;
        return;
      } else {
        this.showBlockUILoading = false;
      }
    }

    const NO_MATCH_ERROR = 3
    if (routerEvent instanceof NavigationError
      && routerEvent.type === NO_MATCH_ERROR) {
      this.router.navigate(['/login'])
    }

    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.showBlockUILoading = false;
      return;
    }
  }

  normalizeLanguageLocale(defaultLanguage: string) {
    try {
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale
      // https://datatracker.ietf.org/doc/html/rfc5646
      return new Intl.Locale(defaultLanguage).toLocaleString();
    } catch {
      return 'en'; // This should NOT happen, but if it does, default to 'en'.
    }
  }
}
